import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n-context";
import { SPONSOR_TIERS } from "@/lib/booth-data";
import { Button } from "@/components/ui/button";
import { Check, Crown, ArrowRight } from "lucide-react";

export default function SponsorsPage() {
  const { t } = useLang();
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-14 max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">
          Sponsorship
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold">
          Align your brand with 68 years of friendship.
        </h1>
        <p className="text-muted-foreground mt-4 text-lg">
          IJFF 2026 is the largest Indonesia-Japan cultural event in Tokyo, expecting tens of thousands of
          visitors across two days. Choose the tier that fits your brand goals.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {SPONSOR_TIERS.map((tier) => (
          <div
            key={tier.key}
            className={`relative rounded-3xl p-7 border-2 hover:-translate-y-1 transition-transform ${
              tier.key === "co_partner"
                ? "bg-black text-white border-black"
                : "bg-white border-border"
            }`}
            data-testid={`tier-${tier.key}`}
          >
            {tier.key === "co_partner" && (
              <div className="absolute -top-3 left-6 bg-brand-gold text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" /> Top tier
              </div>
            )}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full" style={{ backgroundColor: tier.color }} />
              <div className="font-display text-xl font-bold">{tier.name}</div>
            </div>
            <div className="mt-2">
              <div className={`text-[10px] uppercase tracking-widest ${tier.key === "co_partner" ? "text-white/60" : "text-muted-foreground"}`}>
                Contribution
              </div>
              <div className="font-display text-3xl font-bold mt-1">
                ¥ {tier.priceJpy}
              </div>
              <div className={`text-xs mt-1 ${tier.key === "co_partner" ? "text-white/70" : "text-muted-foreground"}`}>
                {tier.booths}
              </div>
            </div>
            {tier.note && (
              <div className={`mt-3 text-xs italic ${tier.key === "co_partner" ? "text-white/70" : "text-brand-red"}`}>
                {tier.note}
              </div>
            )}
            <ul className={`mt-5 space-y-1.5 text-xs ${tier.key === "co_partner" ? "text-white/90" : ""}`}>
              {tier.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${tier.key === "co_partner" ? "text-brand-gold" : "text-brand-red"}`} />
                  <span className="leading-snug">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-brand-canvas rounded-2xl p-5 md:p-6 text-xs text-muted-foreground leading-relaxed">
        <strong className="text-foreground">Booth specs (all tiers):</strong> Each 3.6 × 3.6 m booth includes
        2 tables, 4 chairs, fluorescent lighting, 1.0 kW power supply, cord reel, and partition panels.
        Sponsors are responsible for booth staffing and operation. Any equipment beyond the standard package
        is provided by the sponsor.
      </div>

      <div className="mt-16 bg-brand-canvas rounded-3xl p-10 md:p-14 grid md:grid-cols-3 gap-10 items-center">
        <div className="md:col-span-2">
          <h3 className="font-display text-3xl md:text-4xl font-bold">
            Ready to become a sponsor?
          </h3>
          <p className="text-muted-foreground mt-3 max-w-xl">
            Sign in with Google to submit your sponsorship intent. Our team will send you the full deck
            and contract within 3 business days. Your submission is private — only you and the organizer can see it.
          </p>
          <p className="text-sm mt-4">
            Or reach us directly:{" "}
            <a href="mailto:admin@nationsbridge.jp" className="text-brand-red font-semibold hover:underline" data-testid="sponsors-contact-email">
              admin@nationsbridge.jp
            </a>
          </p>
        </div>
        <Button asChild size="lg" className="rounded-full h-12 bg-brand-red hover:bg-brand-red-dark text-white" data-testid="sponsors-cta-register">
          <Link to="/register/sponsor">
            Register as sponsor <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
